<script setup lang="ts">
// Page V2 « Onboarding / Offboarding » — liste des demandes de dotation/restitution
// matériel d'un collaborateur. Données via useOnboardingStore (API boostcore /material ;
// Lucca auto ou saisie manuelle). KPIs, filtre par type, navigation vers le détail /onboarding/:id.
// Design : cohérent avec ContactsListPage / ExportsCegidAdminPage (cf. MASTER.md).
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  ArrowLeftOnRectangleIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  BoltIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  InboxStackIcon,
  PlusIcon,
  UserMinusIcon,
  UsersIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import {
  CASE_SOURCE_META,
  CASE_STATUS_META,
  CASE_TYPE_META,
  computeProgress,
  isReplacementActive,
  REPLACEMENT_SOURCE_META,
  replacementProgress,
} from '@/v2/onboarding/catalog'
import type { CaseStatus, CaseType, OnboardingCase } from '@/v2/onboarding/types'
import OnbProgressBar from '@/v2/components/onboarding/OnbProgressBar.vue'
import OnbReplacementStatusBadge from '@/v2/components/onboarding/OnbReplacementStatusBadge.vue'
import OnbReplacementDialog from '@/v2/components/onboarding/OnbReplacementDialog.vue'

const router = useRouter()
const store = useOnboardingStore()
const { filteredCases, typeFilter, counts, loading, error, replacementTickets } = storeToRefs(store)

// Chargement initial des données (API) — une seule fois au montage.
onMounted(() => store.ensureLoaded())

// ── Filtre par type ──────────────────────────────────────────────────────────
const TYPE_FILTERS: { value: 'ALL' | CaseType; label: string }[] = [
  { value: 'ALL', label: 'Tous' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'OFFBOARDING', label: 'Offboarding' },
]

// ── KPIs ─────────────────────────────────────────────────────────────────────
const KPI_CARDS: { key: keyof typeof counts.value; label: string; icon: typeof InboxStackIcon }[] = [
  { key: 'total', label: 'Total dossiers', icon: InboxStackIcon },
  { key: 'inProgress', label: 'En cours', icon: ClockIcon },
  { key: 'draft', label: 'Brouillons', icon: DocumentIcon },
  { key: 'offboarding', label: 'Départs', icon: UserMinusIcon },
  { key: 'activeReplacements', label: 'Remplacements', icon: ArrowPathIcon },
]

// ── Badges statut dossier (icône + couleur + label, jamais la couleur seule) ──
const STATUS_ICONS: Record<CaseStatus, typeof DocumentIcon> = {
  DRAFT: DocumentIcon,
  IN_PROGRESS: ClockIcon,
  DONE: CheckCircleIcon,
}

// ── Badges type dossier ───────────────────────────────────────────────────────
const TYPE_META: Record<CaseType, { badgeClass: string; icon: typeof ArrowRightOnRectangleIcon }> = {
  ONBOARDING: { badgeClass: 'badge-success', icon: ArrowRightOnRectangleIcon },
  OFFBOARDING: { badgeClass: 'badge-warning', icon: ArrowLeftOnRectangleIcon },
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function fullName(c: OnboardingCase): string {
  return `${c.collaborator.firstName} ${c.collaborator.lastName}`.trim()
}

/** Date pivot du dossier : arrivée (onboarding) ou sortie (offboarding). */
function pivotDate(c: OnboardingCase): string {
  const iso = c.type === 'OFFBOARDING' ? c.exitDate : c.arrivalDate
  return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'
}

function goTo(c: OnboardingCase) {
  router.push(`/onboarding/${c.id}`)
}

/** Active une ligne au clavier (Entrée / Espace) — ligne cliquable accessible. */
function onRowKey(c: OnboardingCase) {
  goTo(c)
}

// ── Remplacements en cours (tous collaborateurs) — visibles dès l'accueil ─────
const activeReplacements = computed(() =>
  replacementTickets.value
    .filter((t) => isReplacementActive(t.status))
    .map((t) => {
      const col = store.getCase(t.caseId)?.collaborator
      return {
        ticket: t,
        name: col ? `${col.firstName} ${col.lastName}` : t.collaboratorEmail,
        userUuid: t.userUuid ?? col?.userUuid ?? '',
      }
    }),
)

function formatDate(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'
}

/** Ouvre la fiche collaborateur ancrée sur le remplacement concerné. */
function goToReplacement(userUuid: string, ticketId: string) {
  router.push(`/onboarding/collaborateurs/${encodeURIComponent(userUuid)}#repl-${ticketId}`)
}

// ── Demande de remplacement depuis « Nouvelle demande » (mode complet) ────────
const replaceDialog = ref<InstanceType<typeof OnbReplacementDialog> | null>(null)

function openNewReplacement() {
  ;(document.activeElement as HTMLElement | null)?.blur() // referme le menu déroulant
  replaceDialog.value?.open() // mode complet (sélection collaborateur + objet dans la modale)
}

// Le store a déjà rechargé après création — rien à naviguer. Simple confirmation.
const newReplacementNotice = ref('')

function onNewReplacementCreated(_ticketId: string) {
  newReplacementNotice.value = 'Demande de remplacement créée.'
}
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <!-- ── En-tête ──────────────────────────────────────────────────────────── -->
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="flex items-center gap-2 text-2xl font-bold">
          <IdentificationIcon class="h-6 w-6 text-primary" aria-hidden="true" />
          Matériel collaborateur
        </h1>
        <p class="mt-1 max-w-prose text-base-content/80">
          Suivi du matériel à doter ou restituer pour chaque collaborateur. Les demandes
          proviennent de Lucca (détection automatique) ou d'une saisie manuelle.
        </p>
      </div>
      <div class="flex w-full flex-wrap gap-2 sm:w-auto">
        <RouterLink to="/onboarding/collaborateurs" class="btn btn-ghost w-full sm:w-auto">
          <UsersIcon class="h-5 w-5" aria-hidden="true" />
          Vue par collaborateur
        </RouterLink>
        <div class="dropdown dropdown-end w-full sm:w-auto">
          <div tabindex="0" role="button" class="btn btn-primary w-full sm:w-auto">
            <PlusIcon class="h-5 w-5" aria-hidden="true" />
            Nouvelle demande
            <ChevronDownIcon class="h-4 w-4" aria-hidden="true" />
          </div>
          <ul
            tabindex="0"
            class="dropdown-content menu z-10 mt-1 w-full min-w-60 rounded-box border border-base-300 bg-base-100 p-2 shadow-lg sm:w-72"
          >
            <li>
              <RouterLink to="/onboarding/new">Onboarding / Offboarding</RouterLink>
            </li>
            <li>
              <button type="button" @click="openNewReplacement">Remplacement de matériel</button>
            </li>
          </ul>
        </div>
      </div>
    </header>

    <!-- ── État erreur de chargement (discret) ──────────────────────────────── -->
    <div v-if="error" role="alert" class="alert alert-error mt-6">
      <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
      <span>Impossible de charger les dossiers. Réessayez plus tard.</span>
    </div>

    <!-- ── Confirmation inline (remplacement créé depuis « Nouvelle demande ») ── -->
    <div v-if="newReplacementNotice" role="status" class="alert alert-success mt-6">
      <CheckCircleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
      <span>{{ newReplacementNotice }}</span>
    </div>

    <!-- ── KPIs ─────────────────────────────────────────────────────────────── -->
    <section class="mt-6" aria-labelledby="kpi-title">
      <h2 id="kpi-title" class="sr-only">Indicateurs des dossiers</h2>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <div
          v-for="kpi in KPI_CARDS"
          :key="kpi.key"
          class="card border border-base-300 bg-base-100 shadow-sm"
        >
          <div class="card-body flex-row items-center gap-3 p-4">
            <span class="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-base-200 text-primary">
              <component :is="kpi.icon" class="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p class="text-2xl font-bold tabular-nums leading-none">{{ counts[kpi.key] }}</p>
              <p class="mt-1 text-sm text-base-content/80">{{ kpi.label }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Remplacements en cours (tableau compact, clic → fiche collab) ───────── -->
    <section v-if="activeReplacements.length" class="mt-6" aria-labelledby="repl-title">
      <h2 id="repl-title" class="flex items-center gap-2 text-lg font-semibold">
        <ArrowPathIcon class="h-5 w-5 text-primary" aria-hidden="true" />
        Remplacements en cours
        <span class="badge badge-sm badge-ghost tabular-nums">{{ activeReplacements.length }}</span>
      </h2>
      <!-- Desktop : tableau -->
      <div class="mt-3 hidden overflow-x-auto rounded-xl border border-base-300 bg-base-100 shadow-sm md:block">
        <table class="table">
          <thead>
            <tr>
              <th scope="col">Référence</th>
              <th scope="col">Collaborateur</th>
              <th scope="col" class="hidden md:table-cell">Demande</th>
              <th scope="col" class="w-40">Avancement</th>
              <th scope="col">Statut</th>
              <th scope="col" class="hidden md:table-cell">Source</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in activeReplacements"
              :key="r.ticket.id"
              tabindex="0"
              class="cursor-pointer transition-colors hover motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              @click="goToReplacement(r.userUuid, r.ticket.id)"
              @keydown.enter.prevent="goToReplacement(r.userUuid, r.ticket.id)"
              @keydown.space.prevent="goToReplacement(r.userUuid, r.ticket.id)"
            >
              <td class="font-semibold tabular-nums">{{ r.ticket.reference }}</td>
              <td>{{ r.name }}</td>
              <td class="hidden whitespace-nowrap tabular-nums md:table-cell">{{ formatDate(r.ticket.createdAt) }}</td>
              <td class="w-40"><OnbProgressBar :value="replacementProgress(r.ticket.status)" /></td>
              <td><OnbReplacementStatusBadge :status="r.ticket.status" /></td>
              <td class="hidden text-sm text-base-content/80 md:table-cell">{{ REPLACEMENT_SOURCE_META[r.ticket.source].label }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile : cartes -->
      <ul role="list" class="mt-3 grid gap-3 md:hidden">
        <li v-for="r in activeReplacements" :key="r.ticket.id">
          <RouterLink
            :to="`/onboarding/collaborateurs/${encodeURIComponent(r.userUuid)}#repl-${r.ticket.id}`"
            class="card block border border-base-300 bg-base-100 shadow-sm transition-shadow hover:shadow-md motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            <div class="card-body gap-2 p-4">
              <div class="flex items-center justify-between gap-2">
                <span class="font-semibold tabular-nums">{{ r.ticket.reference }}</span>
                <OnbReplacementStatusBadge :status="r.ticket.status" />
              </div>
              <p>{{ r.name }}</p>
              <OnbProgressBar :value="replacementProgress(r.ticket.status)" />
              <div class="flex justify-between gap-2">
                <span class="tabular-nums">{{ formatDate(r.ticket.createdAt) }}</span>
                <span class="text-sm text-base-content/80">{{ REPLACEMENT_SOURCE_META[r.ticket.source].label }}</span>
              </div>
            </div>
          </RouterLink>
        </li>
      </ul>
    </section>

    <!-- ── Filtre par type ──────────────────────────────────────────────────── -->
    <section class="mt-6" aria-labelledby="filter-title">
      <h2 id="filter-title" class="sr-only">Filtrer par type de dossier</h2>
      <div class="join" role="group" aria-label="Filtrer par type de dossier">
        <button
          v-for="f in TYPE_FILTERS"
          :key="f.value"
          type="button"
          class="btn join-item"
          :class="typeFilter === f.value ? 'btn-primary' : 'btn-ghost'"
          :aria-pressed="typeFilter === f.value"
          @click="store.setTypeFilter(f.value)"
        >
          {{ f.label }}
        </button>
      </div>
    </section>

    <!-- ── Tableau des dossiers ─────────────────────────────────────────────── -->
    <section class="mt-4" aria-labelledby="cases-title">
      <h2 id="cases-title" class="sr-only">Liste des dossiers</h2>
      <!-- Desktop : tableau -->
      <div class="hidden overflow-x-auto rounded-xl border border-base-300 bg-base-100 shadow-sm md:block">
        <table class="table">
          <thead>
            <tr>
              <th scope="col">Référence</th>
              <th scope="col">Collaborateur</th>
              <th scope="col">Type</th>
              <th scope="col" class="hidden md:table-cell">Poste / Entité</th>
              <th scope="col" class="hidden md:table-cell">Date</th>
              <th scope="col" class="w-40">Avancement</th>
              <th scope="col">Statut</th>
              <th scope="col" class="hidden md:table-cell">Source</th>
            </tr>
          </thead>
          <tbody>
            <!-- Chargement (API) -->
            <tr v-if="loading">
              <td colspan="8" class="py-10 text-center">
                <span class="loading loading-spinner" aria-hidden="true"></span>
                <p class="mt-2 text-base-content/80">Chargement des dossiers…</p>
              </td>
            </tr>
            <!-- État vide -->
            <tr v-else-if="!filteredCases.length">
              <td colspan="8" class="py-12 text-center">
                <InboxStackIcon class="mx-auto h-10 w-10 text-base-content/40" aria-hidden="true" />
                <p class="mt-2 text-base-content/80">Aucun dossier pour ce filtre.</p>
              </td>
            </tr>
            <!-- Lignes -->
            <tr
              v-for="c in filteredCases"
              v-else
              :key="c.id"
              tabindex="0"
              class="cursor-pointer transition-colors hover motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              @click="goTo(c)"
              @keydown.enter.prevent="onRowKey(c)"
              @keydown.space.prevent="onRowKey(c)"
            >
              <td>
                <RouterLink
                  :to="`/onboarding/${c.id}`"
                  class="font-semibold text-primary hover:underline"
                  @click.stop
                >
                  {{ c.reference }}
                </RouterLink>
              </td>
              <td>{{ fullName(c) }}</td>
              <td>
                <span class="badge gap-1" :class="TYPE_META[c.type].badgeClass">
                  <component :is="TYPE_META[c.type].icon" class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ CASE_TYPE_META[c.type].short }}
                </span>
              </td>
              <td class="hidden md:table-cell">
                <span class="block">{{ c.collaborator.jobTitle }}</span>
                <span class="block text-sm text-base-content/80">{{ c.collaborator.entity }}</span>
              </td>
              <td class="hidden whitespace-nowrap tabular-nums md:table-cell">{{ pivotDate(c) }}</td>
              <td class="w-40">
                <OnbProgressBar :value="computeProgress(c)" :show-value="true" />
              </td>
              <td>
                <span class="badge gap-1" :class="CASE_STATUS_META[c.status].badgeClass">
                  <component :is="STATUS_ICONS[c.status]" class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ CASE_STATUS_META[c.status].label }}
                </span>
              </td>
              <td class="hidden md:table-cell">
                <span class="inline-flex items-center gap-1 text-sm text-base-content/80">
                  <BoltIcon v-if="c.source === 'LUCCA'" class="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {{ CASE_SOURCE_META[c.source].label }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile : cartes -->
      <ul role="list" class="grid gap-3 md:hidden">
        <!-- Chargement (mock : loading reste false, fallback de sécurité) -->
        <li v-if="loading">
          <div class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body items-center gap-2 p-4 text-center">
              <span class="loading loading-spinner" aria-hidden="true"></span>
              <p class="text-base-content/80">Chargement des dossiers…</p>
            </div>
          </div>
        </li>
        <!-- État vide -->
        <li v-else-if="!filteredCases.length">
          <div class="card border border-base-300 bg-base-100 shadow-sm">
            <div class="card-body items-center gap-2 p-4 text-center">
              <InboxStackIcon class="h-10 w-10 text-base-content/40" aria-hidden="true" />
              <p class="text-base-content/80">Aucun dossier pour ce filtre.</p>
            </div>
          </div>
        </li>
        <!-- Cartes -->
        <li v-for="c in filteredCases" v-else :key="c.id">
          <RouterLink
            :to="`/onboarding/${c.id}`"
            class="card block border border-base-300 bg-base-100 shadow-sm transition-shadow hover:shadow-md motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            <div class="card-body gap-2 p-4">
              <div class="flex items-center justify-between gap-2">
                <span class="font-semibold tabular-nums">{{ c.reference }}</span>
                <span class="badge gap-1" :class="TYPE_META[c.type].badgeClass">
                  <component :is="TYPE_META[c.type].icon" class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ CASE_TYPE_META[c.type].short }}
                </span>
              </div>
              <p>{{ fullName(c) }}</p>
              <p class="break-words text-sm text-base-content/80">
                {{ c.collaborator.jobTitle }} · {{ c.collaborator.entity }}
              </p>
              <OnbProgressBar :value="computeProgress(c)" :show-value="true" />
              <div class="flex flex-wrap justify-between gap-2">
                <span class="badge gap-1" :class="CASE_STATUS_META[c.status].badgeClass">
                  <component :is="STATUS_ICONS[c.status]" class="h-3.5 w-3.5" aria-hidden="true" />
                  {{ CASE_STATUS_META[c.status].label }}
                </span>
                <span class="inline-flex items-center gap-1 text-sm text-base-content/80">
                  <BoltIcon v-if="c.source === 'LUCCA'" class="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {{ CASE_SOURCE_META[c.source].label }}
                </span>
              </div>
            </div>
          </RouterLink>
        </li>
      </ul>
    </section>

    <!-- Demande de remplacement (mode complet, depuis « Nouvelle demande ») -->
    <OnbReplacementDialog ref="replaceDialog" @created="onNewReplacementCreated" />
  </div>
</template>
